import React from 'react';
import { cn } from '@/lib/utils';
import { AZ_ALPHABET, LetterPos, letterAllowedAt } from '../../../utils/initials';

/** Shared building blocks for the İnisiallar screens (dark "stage" look of the buzzer / scoring tools). */

export const Panel: React.FC<{ className?: string; children: React.ReactNode }> = ({ className, children }) => (
  <div className={cn('rounded-2xl border border-white/[0.08] bg-white/[0.04]', className)}>{children}</div>
);

export const PanelTitle: React.FC<{ icon?: React.ReactNode; children: React.ReactNode; aside?: React.ReactNode }> = ({ icon, children, aside }) => (
  <div className="flex items-center gap-2 border-b border-white/5 px-4 py-3.5 sm:px-5">
    {icon}
    <h2 className="min-w-0 flex-1 truncate font-bold text-white">{children}</h2>
    {aside}
  </div>
);

type Tone = 'primary' | 'success' | 'danger' | 'ghost';

const TONES: Record<Tone, string> = {
  primary: 'bg-sky-500 text-white hover:bg-sky-600',
  success: 'bg-emerald-500 text-white hover:bg-emerald-600',
  danger: 'bg-rose-500 text-white hover:bg-rose-600',
  ghost: 'border border-white/10 bg-white/5 text-white/70 hover:text-white hover:bg-white/10',
};

export const Btn: React.FC<React.ButtonHTMLAttributes<HTMLButtonElement> & { tone?: Tone }> = ({ tone = 'primary', className, type = 'button', ...props }) => (
  <button
    type={type}
    className={cn(
      'inline-flex items-center justify-center gap-1.5 rounded-xl px-4 py-2.5 text-sm font-bold transition disabled:cursor-not-allowed disabled:opacity-40',
      TONES[tone],
      className
    )}
    {...props}
  />
);

export const inputClass =
  'w-full min-w-0 rounded-[0.625rem] border border-white/10 bg-white/5 px-3 py-2.5 text-white placeholder:text-white/30 focus:border-sky-400/60 focus:outline-none';

export const Field: React.FC<{ label: string; hint?: string; children: React.ReactNode }> = ({ label, hint, children }) => (
  <label className="flex flex-col gap-1.5">
    <span className="text-sm font-semibold text-white/80">{label}</span>
    {children}
    {hint && <span className="text-xs text-white/40">{hint}</span>}
  </label>
);

/** Row of mutually exclusive options. */
export function Segmented<T extends string | number>({
  value,
  options,
  onChange,
}: {
  value: T;
  options: { value: T; label: string }[];
  onChange: (value: T) => void;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map(o => (
        <button
          key={String(o.value)}
          type="button"
          onClick={() => onChange(o.value)}
          aria-pressed={o.value === value}
          className={cn(
            'rounded-[0.625rem] border px-3 py-2 text-sm font-semibold transition',
            o.value === value ? 'border-sky-400/60 bg-sky-500/20 text-sky-100' : 'border-white/10 bg-white/5 text-white/60 hover:text-white'
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/** The 32 letters of the Azerbaijani alphabet as a pickable grid. */
export const LetterGrid: React.FC<{ value: string; pos: LetterPos; onChange: (letter: string) => void }> = ({ value, pos, onChange }) => (
  <div className="grid grid-cols-8 gap-1.5">
    {AZ_ALPHABET.map(letter => {
      const allowed = letterAllowedAt(letter, pos);
      return (
        <button
          key={letter}
          type="button"
          disabled={!allowed}
          onClick={() => onChange(letter)}
          aria-pressed={letter === value}
          title={allowed ? letter : 'Bu hərflə söz başlamır'}
          className={cn(
            'aspect-square rounded-lg border text-base font-black transition sm:text-lg',
            letter === value ? 'border-amber-300 bg-amber-400 text-gray-900' : 'border-white/10 bg-white/5 text-white/80 hover:bg-white/10',
            !allowed && 'cursor-not-allowed opacity-20'
          )}
        >
          {letter}
        </button>
      );
    })}
  </div>
);

/** Big pair of round letters, e.g. "J · D". */
export const LetterPair: React.FC<{ name: string; surname: string; className?: string }> = ({ name, surname, className }) => (
  <div className={cn('flex items-center justify-center gap-3 font-black tabular-nums text-amber-300', className)}>
    <span>{name}</span>
    <span className="text-white/20">·</span>
    <span>{surname}</span>
  </div>
);
